import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  ocrReadingPage,
  createBookTopic,
  updateReadingPDFPage,
  translateReadingSentence,
  correctReadingOcrWords,
  withRetryTime,
  authenticatedPdfSource,
} from "../../api";

import AddWordPopover from "../AddWordPopover";
import { orderOcrIndices, rangeBetween, textForOrdered } from "../../utils/ocrReadingOrder";
import WordMorphology from "../WordMorphology";
import IrabUpsell from "../IrabUpsell";
import ReadingUpsell from "./ReadingUpsell";
import useIrabAnalysis from "../../hooks/useIrabAnalysis";
import { morphTokenKind } from "../../utils/arabicMorphology";

pdfjs.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + "/pdf.worker.min.js";

const ARABIC = /[؀-ۿ]/;
const cleanWord = (raw) => (raw || "").replace(/^[^؀-ۿ]+|[^؀-ۿ]+$/g, "");

/**
 * PdfReader — the reader working surface, shared between the in-app Reading
 * overlay (chrome + library in Reading.js) and the public /reader page
 * (PublicReaderPage.js), the same split as SarfPanel/TypingPanel.
 *
 * context="app" (default): `pdf` is a server object ({id, file_url, title,
 * last_page}) — server OCR cache, resume position, OCR corrections, and the
 * select-for-topic mode all work.
 * context="public": `pdf` is {file: File, title} — the PDF never leaves the
 * browser. No server cache (OCR boxes live in an in-session Map), no resume,
 * no topic mode; 429s from the free tier render a signup upsell.
 * context="topic": `pdf` is an admin-attached TopicPDF ({id, file_url, title,
 * last_page}) — shared across users, so the OCR cache is global (first detect
 * pays, everyone else hits it free) and OCR corrections stay session-local.
 * No topic mode; the wrapper (TopicPdfViewer) owns the resume position.
 */
export default function PdfReader({ pdf, onTopicCreated = () => {}, settings = {}, context = "app" }) {
  const isPublic = context === "public";
  const isTopic = context === "topic";
  // The id is either a ReadingPDF pk (app) or a TopicPDF pk (topic) — split so
  // a TopicPDF id can never reach the ReadingPDF-scoped endpoints (resume
  // PATCH, OCR corrections) and vice versa.
  const readingPdfId = !isPublic && !isTopic ? (pdf.id ?? null) : null;
  const topicPdfId = isTopic ? (pdf.id ?? null) : null;
  const rememberPage = settings.rememberPage !== false;
  const ocrHighRes = settings.ocrHighRes !== false;
  // Server PDFs (app + topic) are fetched through their authenticated file
  // endpoint, so pdf.js needs the token on its requests; the public reader
  // passes a local File object straight through. Memoised: a fresh object
  // each render would make react-pdf reload the document every time.
  const documentSource = useMemo(
    () => (pdf.file_url ? authenticatedPdfSource(pdf.file_url) : pdf.file),
    [pdf.file_url, pdf.file],
  );
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(rememberPage && pdf.last_page ? pdf.last_page : 1);
  const [pageWidth, setPageWidth] = useState(700);
  // Touch devices start from a smaller page width, so they need a higher zoom
  // ceiling and coarser steps than desktop.
  const isTouch = typeof window !== "undefined" && !!window.matchMedia
    && window.matchMedia("(pointer: coarse)").matches;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = isTouch ? 6 : 3;
  const ZOOM_STEP = isTouch ? 0.5 : 0.25;
  const [zoom, setZoom] = useState(1); // PDF zoom multiplier
  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const docRef = useRef(null); // pdf.js document proxy (for high-res OCR render)
  // Public mode has no server-side OCR cache (the PDF is local-only) — keep
  // detected boxes per page for this session so paging back is free.
  const sessionOcr = useRef(new Map()); // pageNumber -> {words, width, height}
  const [loadError, setLoadError] = useState(null);
  const [popover, setPopover] = useState(null); // { word, rect }
  // Normalized page-fraction coords (nx/ny/nw/nh ∈ 0..1) + optional structure
  // keys (line/order/…) from the enriched OCR — scaled to CSS px at render
  // time so boxes always track the live canvas size (zoom, resize).
  const [ocrBoxes, setOcrBoxes] = useState([]);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrError, setOcrError] = useState(null);
  const [ocrLimit, setOcrLimit] = useState(null); // { retryAt } — free tier cap hit
  const [pageHasText, setPageHasText] = useState(true); // false → likely a scan
  // Topic-from-selection
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // box indices
  // Range gesture for topic selection: first tap sets an anchor (and selects
  // that word), a second tap on another word selects everything in between.
  const [topicAnchor, setTopicAnchor] = useState(null); // box index | null
  const [makingTopic, setMakingTopic] = useState(false); // preview/edit panel open
  const [topicTitle, setTopicTitle] = useState("");
  const [topicText, setTopicText] = useState(""); // editable, OCR-corrected
  const [enrichAi, setEnrichAi] = useState(true);
  const [topicBusy, setTopicBusy] = useState(false);
  const [topicMsg, setTopicMsg] = useState(null);
  // Iʿrāb mode — tap a start word, tap an end word, analyze the range
  // through the shared Iʿrāb pipeline (same endpoints/quotas/cache as the
  // Iʿrāb tool). Results decorate the OCR boxes in place.
  const [irabMode, setIrabMode] = useState(false);
  const [irabStart, setIrabStart] = useState(null); // box index
  const [irabEnd, setIrabEnd] = useState(null);     // box index
  const [frozenRange, setFrozenRange] = useState(null); // box order snapshot at analyze
  const [structureWord, setStructureWord] = useState(null); // { key, morph }
  const irab = useIrabAnalysis({ context: isTopic ? "topic-pdf" : isPublic ? "reader-public" : "pdf-reader" });
  const ALL_KINDS = { verb: true, noun: true, pronoun: true, prepositions: true };
  // Translate mode — same tap-start / tap-end selection as Iʿrāb mode, but
  // the range is reviewed (fix OCR mistakes) and then translated as one
  // sentence. Server-cached: a repeat of the same sentence costs nothing.
  const [transMode, setTransMode] = useState(false);
  const [transStart, setTransStart] = useState(null); // box index
  const [transEnd, setTransEnd] = useState(null);     // box index
  const [transFrozen, setTransFrozen] = useState(null); // box order snapshot at translate
  const [transReviewing, setTransReviewing] = useState(false); // pre-send edit panel
  const [transWords, setTransWords] = useState([]); // editable word texts (review panel)
  const [transBusy, setTransBusy] = useState(false);
  const [transError, setTransError] = useState(null);
  const [transLimit, setTransLimit] = useState(null); // { retryAt } — free tier cap hit
  const [transResult, setTransResult] = useState(null); // { translation, words, cached }
  const [transRemaining, setTransRemaining] = useState(settings.translationRemaining ?? null);
  const transMaxChars = settings.translationMaxChars ?? 300;

  const orderedIdx = useMemo(() => orderOcrIndices(ocrBoxes), [ocrBoxes]);
  const irabRange = useMemo(() => {
    if (irabStart == null) return [];
    if (irabEnd == null) return [irabStart];
    return rangeBetween(orderedIdx, irabStart, irabEnd);
  }, [orderedIdx, irabStart, irabEnd]);
  const irabText = useMemo(() => textForOrdered(ocrBoxes, irabRange), [ocrBoxes, irabRange]);
  const overLimit = irabText.length > irab.maxChars;
  // token key ("0","1"…) per box index, valid once analysis ran
  const tokenKeyByBox = useMemo(() => {
    if (!frozenRange || !irab.sentence) return new Map();
    if (irab.sentence.split(/\s+/).length !== frozenRange.length) return new Map(); // defensive
    return new Map(frozenRange.map((boxIdx, k) => [boxIdx, String(k)]));
  }, [frozenRange, irab.sentence]);

  const resetIrab = () => {
    setIrabStart(null);
    setIrabEnd(null);
    setFrozenRange(null);
    setStructureWord(null);
    irab.reset();
  };

  const transRange = useMemo(() => {
    if (transStart == null) return [];
    if (transEnd == null) return [transStart];
    return rangeBetween(orderedIdx, transStart, transEnd);
  }, [orderedIdx, transStart, transEnd]);
  const transText = useMemo(() => textForOrdered(ocrBoxes, transRange), [ocrBoxes, transRange]);
  const transOverLimit = transText.length > transMaxChars;
  // cached gloss per box, valid once translated (index-aligned with the range)
  const transGlossByBox = useMemo(() => {
    if (!transFrozen || !transResult?.words) return new Map();
    if (transResult.words.length !== transFrozen.length) return new Map(); // defensive
    return new Map(transFrozen.map((boxIdx, k) => [boxIdx, transResult.words[k]]));
  }, [transFrozen, transResult]);

  const resetTrans = () => {
    setTransStart(null);
    setTransEnd(null);
    setTransFrozen(null);
    setTransReviewing(false);
    setTransWords([]);
    setTransResult(null);
    setTransError(null);
    setTransLimit(null);
  };

  // The daily-remaining seed arrives async with the admin settings; server
  // responses overwrite it afterwards.
  useEffect(() => {
    if (settings.translationRemaining != null) setTransRemaining(settings.translationRemaining);
  }, [settings.translationRemaining]);

  // Desktop hover preview: once a start word is tapped (in any selection
  // mode), hovering another word live-highlights the prospective range, and
  // clicking it confirms. Touch devices never fire these hover states.
  const canHover = typeof window !== "undefined" && !!window.matchMedia
    && window.matchMedia("(hover: hover)").matches;
  const [hoverIdx, setHoverIdx] = useState(null);
  const hoverAnchor = irabMode
    ? (irabStart != null && irabEnd == null ? irabStart : null)
    : transMode
      ? (transStart != null && transEnd == null && !transResult && !transReviewing ? transStart : null)
      : selectMode ? topicAnchor : null;
  const hoverRange = useMemo(() => {
    if (hoverAnchor == null || hoverIdx == null || hoverIdx === hoverAnchor) return [];
    return rangeBetween(orderedIdx, hoverAnchor, hoverIdx);
  }, [hoverAnchor, hoverIdx, orderedIdx]);
  const hoverSet = useMemo(() => new Set(hoverRange), [hoverRange]);

  const containerRef = useRef(null);
  const pageWrapRef = useRef(null);

  // OCR overlay is tied to the current page — clear it when the page (not the
  // zoom/size — normalized coords survive those) changes.
  useEffect(() => {
    setOcrBoxes([]);
    setOcrError(null);
    setOcrLimit(null);
    setSelected(new Set());
    setMakingTopic(false);
    setTopicMsg(null);
    setIrabStart(null);
    setIrabEnd(null);
    setFrozenRange(null);
    setStructureWord(null);
    setTransStart(null);
    setTransEnd(null);
    setTransFrozen(null);
    setTransReviewing(false);
    setTransWords([]);
    setTransResult(null);
    setTransError(null);
    setTransLimit(null);
    setTopicAnchor(null);
    setHoverIdx(null);
  }, [pageNumber, pdf]);

  // Reconstruct selected text in reading order (structure keys when the OCR
  // provided them, else rows top→bottom / RTL within a row).
  const buildSelectedText = () => {
    const ordered = orderOcrIndices(ocrBoxes).filter((i) => selected.has(i));
    return textForOrdered(ocrBoxes, ordered);
  };

  // Open the preview/edit panel, prefilled with the (correctable) selected text.
  const openTopicPreview = () => {
    setTopicText(buildSelectedText());
    setTopicTitle(`${pdf.title || "Reading"} — p.${pageNumber}`);
    setTopicMsg(null);
    setMakingTopic(true);
  };

  const handleCreateTopic = async () => {
    const text = topicText.trim();
    if (!text) return;
    setTopicBusy(true);
    setTopicMsg(null);
    try {
      const res = await createBookTopic({
        pdfId: pdf.id, pageNumber, text, title: topicTitle.trim(), enrichAi,
      });
      let msg = `Created topic “${res.name}”. It's in your topics & guided journey.`;
      if (enrichAi && res.ai_limit_reached) {
        msg += " " + withRetryTime("AI enrich skipped — daily limit reached.", res.retry_at);
      } else if (enrichAi && res.ai_enriched) {
        msg += " Definitions & translations added.";
      }
      if (res.pending_review_count > 0) {
        msg += ` ${res.pending_review_count} new word(s) sent to admin for review.`;
      }
      setTopicMsg(msg);
      setSelected(new Set());
      setTopicAnchor(null);
      setMakingTopic(false);
      setSelectMode(false);
      onTopicCreated();
    } catch (e) {
      setTopicMsg(e.message || "Could not create topic.");
    } finally {
      setTopicBusy(false);
    }
  };

  // Store OCR words as page fractions (independent of zoom/canvas size).
  const applyOcr = (words, width, height) => {
    const canvas = pageWrapRef.current?.querySelector("canvas");
    if (!canvas || !words || !words.length) return false;
    const W = width || canvas.width;
    const H = height || canvas.height;
    setCanvasSize({ w: canvas.clientWidth, h: canvas.clientHeight });
    setOcrBoxes(
      words.map((w) => ({
        text: w.text,
        nx: w.x / W, ny: w.y / H, nw: w.w / W, nh: w.h / H,
        block: w.block, para: w.para, line: w.line, order: w.order,
      }))
    );
    return true;
  };

  // On every page render: retrack the canvas size (zoom/resize) and, if this
  // page has no boxes yet, probe the server cache (free) — or, in public
  // mode, this session's local cache (the server has nothing for a local PDF).
  const handleRenderSuccess = async () => {
    const canvas = pageWrapRef.current?.querySelector("canvas");
    if (canvas) setCanvasSize({ w: canvas.clientWidth, h: canvas.clientHeight });
    if (ocrBoxes.length > 0) return; // already loaded — don't clobber state
    if (!readingPdfId && !topicPdfId) {
      const hit = sessionOcr.current.get(pageNumber);
      if (hit) applyOcr(hit.words, hit.width, hit.height);
      return;
    }
    try {
      const { width, height, words } = await ocrReadingPage({ pdfId: readingPdfId, topicPdfId, pageNumber });
      if (words && words.length) applyOcr(words, width, height);
    } catch {
      /* no cache — leave it to the Detect button */
    }
  };

  // Persist the resume position when the page changes (if enabled). Topic PDFs
  // are shared, so their position is remembered per-device by the wrapper.
  useEffect(() => {
    if (readingPdfId && rememberPage && pageNumber >= 1) updateReadingPDFPage(readingPdfId, pageNumber);
    if (isTopic && topicPdfId && rememberPage && pageNumber >= 1) {
      try { localStorage.setItem(`topicPdfLastPage:${topicPdfId}`, String(pageNumber)); } catch { /* private mode */ }
    }
  }, [pageNumber, rememberPage, readingPdfId, isTopic, topicPdfId]);

  // Render the current page to a high-resolution offscreen canvas for OCR.
  const renderHiRes = async () => {
    if (!docRef.current) return null;
    const page = await docRef.current.getPage(pageNumber);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(3, Math.max(1, 1600 / base.width));
    const vp = page.getViewport({ scale });
    const off = document.createElement("canvas");
    off.width = Math.ceil(vp.width);
    off.height = Math.ceil(vp.height);
    await page.render({ canvasContext: off.getContext("2d"), viewport: vp }).promise;
    return off.toDataURL("image/png");
  };

  const detectText = async () => {
    const canvas = pageWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    setOcrBusy(true);
    setOcrError(null);
    setOcrLimit(null);
    try {
      let dataUrl = null;
      if (ocrHighRes) {
        try { dataUrl = await renderHiRes(); } catch { dataUrl = null; }
      }
      if (!dataUrl) dataUrl = canvas.toDataURL("image/png");
      const { width, height, words } = await ocrReadingPage({
        imageBase64: dataUrl,
        ...(readingPdfId ? { pdfId: readingPdfId, pageNumber } : {}),
        ...(topicPdfId ? { topicPdfId, pageNumber } : {}),
        // Re-detect on a cached shared page: ask the server to redo it. Only
        // staff are allowed to overwrite the global cache; for everyone else
        // the server ignores force and returns the cached page.
        force: !!topicPdfId && ocrBoxes.length > 0,
      });
      if (!words || words.length === 0) {
        setOcrError("No Arabic text detected on this page.");
        setOcrBoxes([]);
        return;
      }
      if (!readingPdfId && !topicPdfId) sessionOcr.current.set(pageNumber, { words, width, height });
      applyOcr(words, width, height);
    } catch (e) {
      if (e.signupCta) {
        setOcrLimit({ retryAt: e.retryAt });
      } else {
        setOcrError(e.message || "Could not detect text.");
      }
    } finally {
      setOcrBusy(false);
    }
  };

  const measure = useCallback(() => {
    if (containerRef.current) {
      setPageWidth(Math.max(260, Math.min(900, containerRef.current.clientWidth - 32)));
    }
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // Wrap each Arabic token in a clickable span inside the PDF text layer.
  const customTextRenderer = useCallback((textItem) => {
    const str = textItem.str || "";
    if (!ARABIC.test(str)) return str;
    return str
      .split(/(\s+)/)
      .map((tok) => {
        if (!ARABIC.test(tok)) return tok;
        const safe = tok.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        return `<span class="rw">${safe}</span>`;
      })
      .join("");
  }, []);

  const handleIrabBoxClick = (i) => {
    // analyzed word → structure diagram
    const key = tokenKeyByBox.get(i);
    const entry = key != null ? irab.tokens?.[key] : null;
    if (entry?.morphology) {
      setStructureWord({ key, morph: entry.morphology });
      return;
    }
    // selection state machine
    if (irabStart == null) {
      setIrabStart(i);
    } else if (irabEnd == null) {
      if (i === irabStart) { setIrabStart(null); return; } // tap start again = cancel
      setIrabEnd(i);
    } else {
      // new selection: this tap becomes the new start
      setIrabStart(i);
      setIrabEnd(null);
      setFrozenRange(null);
      irab.reset();
    }
  };

  const irabBoxClass = (i) => {
    if (!irabMode) {
      // topic select mode (or no mode): selected words, range anchor, hover preview
      if (selected.has(i)) return "bg-amber-400/55";
      if (selectMode && i === topicAnchor) return "ring-2 ring-amber-500 bg-amber-400/50";
      if (selectMode && hoverSet.has(i)) return "bg-amber-200/45";
      return "hover:bg-amber-300/40";
    }
    const key = tokenKeyByBox.get(i);
    const entry = key != null ? irab.tokens?.[key] : null;
    if (entry?.morphology) {
      const kind = morphTokenKind(entry.morphology, ALL_KINDS);
      return kind ? `tokb-${kind} cursor-pointer` : "bg-amber-200/40 cursor-pointer";
    }
    if (i === irabStart && irabEnd == null) return "ring-2 ring-amber-500 bg-amber-400/50";
    if (hoverSet.has(i)) return "bg-amber-200/45";
    if (irabRange.includes(i)) return "bg-amber-300/45";
    return "hover:bg-amber-300/40";
  };

  // Topic select: single taps toggle a word (unchanged); tapping a word then
  // another ADDS the whole range in between — same gesture as Iʿrāb/translate,
  // but accumulating so several sentences can be picked. Tapping the anchor
  // again deselects it and cancels the pending range.
  const handleTopicBoxClick = (i) => {
    if (topicAnchor != null && topicAnchor !== i) {
      const range = rangeBetween(orderedIdx, topicAnchor, i);
      setSelected((prev) => {
        const next = new Set(prev);
        range.forEach((k) => next.add(k));
        return next;
      });
      setTopicAnchor(null);
    } else if (selected.has(i)) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(i);
        return next;
      });
      if (topicAnchor === i) setTopicAnchor(null);
    } else {
      setSelected((prev) => new Set(prev).add(i));
      setTopicAnchor(i);
    }
    setHoverIdx(null);
  };

  const handleIrabAnalyze = () => {
    if (!irabRange.length || overLimit || irab.loading) return;
    setFrozenRange(irabRange);
    irab.analyze(irabText);
  };

  const handleTransBoxClick = (i, rect) => {
    // translated word → popover prefilled from the cached contextual gloss
    const gloss = transGlossByBox.get(i);
    if (gloss) {
      setPopover({ word: cleanWord(ocrBoxes[i].text), rect, prefill: gloss });
      return;
    }
    if (transReviewing) return; // selection is frozen while reviewing the words
    // selection state machine — same as Iʿrāb mode
    if (transStart == null) {
      setTransStart(i);
    } else if (transEnd == null) {
      if (i === transStart) { setTransStart(null); return; } // tap start again = cancel
      setTransEnd(i);
    } else {
      // new selection: this tap becomes the new start
      setTransStart(i);
      setTransEnd(null);
      setTransFrozen(null);
      setTransResult(null);
      setTransError(null);
    }
  };

  const transBoxClass = (i) => {
    if (transGlossByBox.has(i)) return "bg-teal-400/45 cursor-pointer";
    if (i === transStart && transEnd == null) return "ring-2 ring-teal-500 bg-teal-400/50";
    if (hoverSet.has(i)) return "bg-teal-200/45";
    if (transRange.includes(i)) return "bg-teal-300/45";
    return "hover:bg-teal-300/40";
  };

  // Open the pre-send review panel: each detected word becomes editable so
  // OCR mistakes can be fixed before the sentence reaches the AI (and the
  // translation cache is keyed on the corrected text).
  const openTransReview = () => {
    if (!transRange.length || transEnd == null || transOverLimit) return;
    setTransFrozen(transRange);
    setTransWords(transRange.map((i) => ocrBoxes[i].text));
    setTransResult(null);
    setTransError(null);
    setTransReviewing(true);
  };

  const handleTranslate = async () => {
    const frozen = transFrozen || [];
    if (!frozen.length || transBusy) return;
    const edited = transWords.map((w) => w.trim());
    if (edited.some((w) => !w)) return;
    setTransBusy(true);
    setTransError(null);
    setTransLimit(null);
    try {
      // Persist OCR fixes first so this page (and its cache keys) stay corrected.
      // A local-only PDF has no server row to correct, and a shared topic PDF's
      // global cache must not be rewritten by one reader — in both cases the
      // local box edit below still fixes the words this session sees.
      const corrections = frozen
        .map((boxIdx, k) => ({ index: boxIdx, text: edited[k] }))
        .filter((c, k) => c.text !== ocrBoxes[frozen[k]].text);
      if (corrections.length) {
        setOcrBoxes((prev) => {
          const next = prev.slice();
          corrections.forEach((c) => { next[c.index] = { ...next[c.index], text: c.text }; });
          return next;
        });
        if (readingPdfId) {
          correctReadingOcrWords({ pdfId: readingPdfId, pageNumber, corrections }).catch(() => {});
        }
      }
      const res = await translateReadingSentence(edited.join(" "));
      setTransResult(res);
      if (res.remaining != null) setTransRemaining(res.remaining);
      setTransReviewing(false);
    } catch (e) {
      if (e.signupCta) {
        setTransLimit({ retryAt: e.retryAt });
        setTransReviewing(false);
      } else {
        setTransError(e.message || "Could not translate.");
      }
    } finally {
      setTransBusy(false);
    }
  };

  const handleClick = (e) => {
    if (irabMode || transMode) return; // word taps belong to the active mode, not the popover
    const t = e.target;
    if (t && t.classList && t.classList.contains("rw")) {
      const word = cleanWord(t.textContent);
      if (!word) return;
      const r = t.getBoundingClientRect();
      setPopover({ word, rect: r });
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <style>{`.react-pdf__Page__textContent .rw{cursor:pointer;border-radius:2px;}
        .react-pdf__Page__textContent .rw:hover{background:rgba(250,204,21,.45);}`}</style>

      {/* Pager */}
      {numPages && (
        <div className="flex flex-wrap items-center justify-center gap-2 py-2 border-b border-slate-100 text-sm shrink-0">
          <button
            onClick={() => setPageNumber((n) => Math.max(1, n - 1))}
            disabled={pageNumber <= 1}
            className="px-2.5 py-1 rounded-lg border border-slate-200 text-ink-600 hover:bg-slate-50 disabled:opacity-40"
          >Prev</button>
          <span className="text-ink-500">Page {pageNumber} / {numPages}</span>
          <button
            onClick={() => setPageNumber((n) => Math.min(numPages, n + 1))}
            disabled={pageNumber >= numPages}
            className="px-2.5 py-1 rounded-lg border border-slate-200 text-ink-600 hover:bg-slate-50 disabled:opacity-40"
          >Next</button>
          <button
            onClick={detectText}
            disabled={ocrBusy}
            title="Scanned page? Detect Arabic words so you can tap them."
            className="px-2.5 py-1 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-50"
          >
            {ocrBusy ? "Detecting…" : ocrBoxes.length ? "Re-detect" : "Detect text"}
          </button>
          {/* Zoom the PDF itself (independent of browser/page zoom) */}
          <div className="inline-flex items-center rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={zoomOut}
              disabled={zoom <= ZOOM_MIN}
              title="Zoom out"
              aria-label="Zoom out"
              className="px-2.5 py-1 text-ink-600 hover:bg-slate-50 disabled:opacity-40"
            >−</button>
            <button
              onClick={() => setZoom(1)}
              title="Reset zoom"
              aria-label="Reset zoom"
              className="px-1.5 py-1 text-xs text-ink-500 tabular-nums border-x border-slate-200 hover:bg-slate-50 min-w-[3rem]"
            >{Math.round(zoom * 100)}%</button>
            <button
              onClick={zoomIn}
              disabled={zoom >= ZOOM_MAX}
              title="Zoom in"
              aria-label="Zoom in"
              className="px-2.5 py-1 text-ink-600 hover:bg-slate-50 disabled:opacity-40"
            >+</button>
          </div>
        </div>
      )}
      {ocrError && <p className="text-xs text-amber-700 text-center py-1 shrink-0">{ocrError}</p>}
      {ocrLimit && (
        <div className="dark-scope px-4 py-1 shrink-0">
          <ReadingUpsell kind="ocr" retryAt={ocrLimit.retryAt} context={context} />
        </div>
      )}

      {/* Scanned-page hint: no selectable text and nothing detected yet. */}
      {!pageHasText && ocrBoxes.length === 0 && !ocrBusy && (
        <p className="text-xs text-ink-500 text-center py-1.5 bg-violet-50 border-b border-violet-100 shrink-0">
          This page has no selectable text (looks scanned) — tap <span className="font-semibold text-violet-700">Detect text</span> to make words tappable.
        </p>
      )}

      {/* Selection toolbar. Idle: the three mode buttons. Once a mode is on,
          the others hide and only the active pill (✕ = exit) + that mode's
          own controls remain — one focused toolbar per mode. */}
      {ocrBoxes.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 py-2 border-b border-slate-100 text-sm shrink-0">
          {!irabMode && !transMode && !isPublic && !isTopic && (
            <button
              onClick={() => {
                setSelectMode((v) => !v);
                setTopicAnchor(null);
                setMakingTopic(false);
              }}
              title={selectMode ? "Exit topic selection" : "Pick words from the page to build a topic"}
              className={`px-2.5 py-1 rounded-lg border text-xs font-medium ${
                selectMode ? "bg-ink-600 text-white border-ink-600" : "border-slate-200 text-ink-600 hover:bg-slate-50"
              }`}
            >
              {selectMode ? "✕ Select for topic" : "Select for topic"}
            </button>
          )}
          {selectMode && (
            <>
              <span className="text-xs text-ink-500">Tap words, or two ends of a range</span>
              <button
                onClick={() => { setSelected(new Set(ocrBoxes.map((_, i) => i))); setTopicAnchor(null); }}
                className="px-2.5 py-1 rounded-lg border border-slate-200 text-ink-600 hover:bg-slate-50 text-xs"
              >Select whole page</button>
              <button
                onClick={() => { setSelected(new Set()); setTopicAnchor(null); }}
                disabled={selected.size === 0}
                className="px-2.5 py-1 rounded-lg border border-slate-200 text-ink-600 hover:bg-slate-50 text-xs disabled:opacity-40"
              >Clear</button>
              <button
                onClick={openTopicPreview}
                disabled={selected.size === 0}
                className="px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-40"
              >Create topic ({selected.size})</button>
            </>
          )}
          {!selectMode && !transMode && (
            <button
              onClick={() => {
                setIrabMode((v) => {
                  if (v) resetIrab();
                  return !v;
                });
                setSelected(new Set()); // leftover topic selection from an exited session
                setTopicAnchor(null);
                setMakingTopic(false);
              }}
              title={irabMode ? "Exit Iʿrāb mode" : "Select a sentence and get its full word-by-word iʿrāb"}
              className={`px-2.5 py-1 rounded-lg border text-xs font-medium ${
                irabMode ? "bg-amber-600 text-white border-amber-600" : "border-amber-400 text-amber-600 hover:bg-amber-400/15"
              }`}
            >
              {irabMode ? "✕ Iʿrāb mode" : "◇ Iʿrāb mode"}
            </button>
          )}
          {!selectMode && !irabMode && (
            <button
              onClick={() => {
                setTransMode((v) => {
                  if (v) resetTrans();
                  return !v;
                });
                setSelected(new Set()); // leftover topic selection from an exited session
                setTopicAnchor(null);
                setMakingTopic(false);
              }}
              title={transMode ? "Exit translate mode" : "Select a sentence, fix any OCR mistakes, and translate it as a whole"}
              className={`px-2.5 py-1 rounded-lg border text-xs font-medium ${
                transMode ? "bg-teal-600 text-white border-teal-600" : "border-teal-400 text-teal-600 hover:bg-teal-400/15"
              }`}
            >
              {transMode ? "✕ Translate mode" : "⇄ Translate mode"}
            </button>
          )}
        </div>
      )}
      {irabMode && (
        <div className="px-4 py-2 border-b border-slate-100 shrink-0">
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
            <span className="text-ink-600">
              {irabStart == null
                ? "Tap the first word of the sentence"
                : irabEnd == null
                  ? "Now tap the last word"
                  : `${irabRange.length} words · ${irabText.length}/${irab.maxChars}`}
            </span>
            {irab.remaining != null && (
              <span className="text-ink-400">· {irab.remaining} sentences left today</span>
            )}
            {irab.tier === "free" && irab.refineRemaining != null && (
              <span className="text-ink-400">· {irab.refineRemaining} AI checks left</span>
            )}
            <button
              onClick={resetIrab}
              disabled={irabStart == null && !irab.tokens}
              className="px-2.5 py-1 rounded-lg border border-slate-200 text-ink-600 hover:bg-slate-50 text-xs disabled:opacity-40"
            >Clear</button>
            <button
              onClick={handleIrabAnalyze}
              disabled={!irabRange.length || irabEnd == null || overLimit || irab.loading}
              className="px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-40"
            >{irab.loading ? "Analyzing…" : "Analyze — أَعْرِب"}</button>
          </div>
          {overLimit && (
            <p className="text-[11px] text-amber-700 text-center mt-1">
              Selection too long — max {irab.maxChars} characters. Choose a nearer end word.
            </p>
          )}
          {irab.error && !irab.upsell && (
            <p className="text-[11px] text-rose-700 text-center mt-1">{irab.error}</p>
          )}
          {irab.notice && !irab.error && (
            <p className="text-[11px] text-amber-700 text-center mt-1">{irab.notice}</p>
          )}
          {irab.tokens && (
            <p className="text-[11px] text-ink-500 text-center mt-1">
              Tap a colored word for its structure and iʿrāb.
              {irab.refining.size > 0 && " Words with a dot are being checked by the AI…"}
            </p>
          )}
          {irab.upsell && (
            <div className="dark-scope mt-1">
              <IrabUpsell kind={irab.upsell} retryAt={irab.upsellRetryAt}
                subscriberDaily={irab.subscriberDaily} context={isTopic ? "topic-pdf" : isPublic ? "reader-public" : "pdf-reader"} />
            </div>
          )}
        </div>
      )}
      {transMode && (
        <div className="px-4 py-2 border-b border-slate-100 shrink-0">
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
            <span className="text-ink-600">
              {transResult
                ? (transResult.cached ? "Translated — from cache (free)" : "Translated")
                : transStart == null
                  ? "Tap the first word of the sentence"
                  : transEnd == null
                    ? "Now tap the last word"
                    : `${transRange.length} words · ${transText.length}/${transMaxChars}`}
            </span>
            {transRemaining != null && (
              <span className="text-ink-400">· {transRemaining} translations left today</span>
            )}
            <button
              onClick={resetTrans}
              disabled={transStart == null && !transResult}
              className="px-2.5 py-1 rounded-lg border border-slate-200 text-ink-600 hover:bg-slate-50 text-xs disabled:opacity-40"
            >Clear</button>
            {!transResult && !transReviewing && (
              <button
                onClick={openTransReview}
                disabled={!transRange.length || transEnd == null || transOverLimit || transBusy}
                className="px-2.5 py-1 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold disabled:opacity-40"
              >Translate — تَرْجِم</button>
            )}
          </div>
          {transOverLimit && (
            <p className="text-[11px] text-amber-700 text-center mt-1">
              Selection too long — max {transMaxChars} characters. Choose a nearer end word.
            </p>
          )}
          {transError && (
            <p className="text-[11px] text-rose-700 text-center mt-1">{transError}</p>
          )}
          {transLimit && (
            <div className="dark-scope mt-1">
              <ReadingUpsell kind="translate" retryAt={transLimit.retryAt} context={context} />
            </div>
          )}
          {transReviewing && (
            <div className="mt-2 space-y-2">
              <p className="text-[11px] text-ink-500 text-center">
                Check the detected words below — fix any OCR mistakes, then translate.
              </p>
              <div dir="rtl" className="flex flex-wrap justify-center gap-1.5">
                {transWords.map((w, k) => (
                  <input
                    key={k}
                    value={w}
                    dir="rtl"
                    size={Math.max(3, w.length + 1)}
                    onChange={(e) => setTransWords((prev) => {
                      const next = prev.slice();
                      next[k] = e.target.value;
                      return next;
                    })}
                    className="px-1.5 py-1 rounded-lg border border-slate-300 text-base text-ink-800 font-arabic text-center"
                  />
                ))}
              </div>
              <div className="flex justify-center gap-2">
                <button
                  onClick={handleTranslate}
                  disabled={transBusy || transWords.some((w) => !w.trim())}
                  className="px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold disabled:opacity-50"
                >{transBusy ? "Translating…" : "Looks right — translate"}</button>
                <button
                  onClick={() => { setTransReviewing(false); setTransFrozen(null); }}
                  disabled={transBusy}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs disabled:opacity-50"
                >Cancel</button>
              </div>
            </div>
          )}
          {transResult && (
            <div className="mt-1">
              <p className="text-sm text-ink-700 text-center" dir="ltr">{transResult.translation}</p>
              <p className="text-[11px] text-ink-500 text-center mt-0.5">
                Tap a highlighted word for its meaning — cached, no extra AI cost.
              </p>
            </div>
          )}
        </div>
      )}
      {makingTopic && (
        <div className="px-4 py-3 border-b border-slate-100 shrink-0 space-y-2 bg-parchment-50">
          <p className="text-xs font-semibold text-ink-600">Review the text (fix any detection mistakes), then create the topic.</p>
          <input
            value={topicTitle}
            onChange={(e) => setTopicTitle(e.target.value)}
            placeholder="Topic title"
            className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm"
          />
          <textarea
            value={topicText}
            onChange={(e) => setTopicText(e.target.value)}
            dir="rtl"
            rows={5}
            placeholder="Arabic text"
            className="w-full px-2.5 py-2 rounded-lg border border-slate-300 text-base text-ink-800 font-arabic"
          />
          <label className="flex items-center gap-2 text-sm text-ink-600">
            <input type="checkbox" checked={enrichAi} onChange={(e) => setEnrichAi(e.target.checked)} />
            Use AI: add harakat, detect noun/verb/particle, define words &amp; translate sentences
          </label>
          <div className="flex gap-2">
            <button
              onClick={handleCreateTopic}
              disabled={topicBusy || !topicText.trim() || !topicTitle.trim()}
              className="px-3 py-1.5 rounded-lg bg-ink-600 hover:bg-ink-700 text-white text-sm font-semibold disabled:opacity-50"
            >{topicBusy ? "Creating…" : "Create topic"}</button>
            <button
              onClick={() => setMakingTopic(false)}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-sm"
            >Cancel</button>
          </div>
        </div>
      )}
      {topicMsg && <p className="text-xs text-emerald-700 text-center py-1 shrink-0">{topicMsg}</p>}

      <div ref={containerRef} className="flex-1 overflow-auto bg-slate-100 py-4" onClick={handleClick}>
        {loadError ? (
          <p className="text-sm text-red-600 p-6">{loadError}</p>
        ) : (
          <Document
            file={documentSource}
            className="w-fit mx-auto"
            onLoadSuccess={(doc) => {
              docRef.current = doc;
              setNumPages(doc.numPages);
              if (pageNumber > doc.numPages) setPageNumber(1); // stale resume position
            }}
            onLoadError={() => setLoadError("Could not open this PDF.")}
            loading={<p className="text-sm text-ink-400 p-6">Loading PDF…</p>}
          >
            <div ref={pageWrapRef} className="relative inline-block">
              <Page
                pageNumber={pageNumber}
                width={Math.round(pageWidth * zoom)}
                renderTextLayer
                renderAnnotationLayer={false}
                customTextRenderer={customTextRenderer}
                onRenderSuccess={handleRenderSuccess}
                onRenderTextLayerSuccess={() => {
                  const tl = pageWrapRef.current?.querySelector(".react-pdf__Page__textContent");
                  setPageHasText(!!(tl && ARABIC.test(tl.textContent || "")));
                }}
              />
              {/* OCR overlay — tappable boxes over a scanned page */}
              {ocrBoxes.length > 0 && (
                <div className="absolute inset-0 z-10">
                  {ocrBoxes.map((b, i) => (
                    <button
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (irabMode) {
                          handleIrabBoxClick(i);
                          return;
                        }
                        if (transMode) {
                          handleTransBoxClick(i, e.currentTarget.getBoundingClientRect());
                          return;
                        }
                        if (selectMode) {
                          handleTopicBoxClick(i);
                          return;
                        }
                        const r = e.currentTarget.getBoundingClientRect();
                        setPopover({ word: cleanWord(b.text), rect: r });
                      }}
                      title={b.text}
                      onMouseEnter={canHover && hoverAnchor != null ? () => setHoverIdx(i) : undefined}
                      onMouseLeave={canHover && hoverAnchor != null ? () => setHoverIdx((h) => (h === i ? null : h)) : undefined}
                      className={`absolute rounded-sm ${transMode ? transBoxClass(i) : irabBoxClass(i)}`}
                      style={{
                        left: b.nx * canvasSize.w,
                        top: b.ny * canvasSize.h,
                        width: b.nw * canvasSize.w,
                        height: b.nh * canvasSize.h,
                      }}
                    >
                      {irabMode && irab.refining.has(tokenKeyByBox.get(i)) && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-spin"
                          style={{ border: "2px solid rgba(180,83,9,.35)", borderTopColor: "#b45309" }} />
                      )}
                      {irabMode
                        && irab.tokens?.[tokenKeyByBox.get(i)]?.source === "openai"
                        && !irab.refining.has(tokenKeyByBox.get(i)) && (
                        <span className="absolute -top-1.5 -right-1 text-[9px] leading-none select-none"
                          style={{ color: "#b45309" }}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Document>
        )}
      </div>

      {popover && !irabMode && (
        <AddWordPopover word={popover.word} anchorRect={popover.rect} prefill={popover.prefill}
          mode={isPublic ? "public" : "app"} onClose={() => setPopover(null)} />
      )}
      {structureWord && (
        <WordMorphology
          key={`${structureWord.key}:${structureWord.morph?.label || ""}`}
          word={structureWord.morph}
          onClose={() => setStructureWord(null)}
          verifiable={irab.isStaff && !!irab.tokens?.[structureWord.key]}
          verified={!!irab.tokens?.[structureWord.key]?.verified}
          onVerify={() =>
            irab.verifyToken(structureWord.key, !irab.tokens?.[structureWord.key]?.verified)
              .catch(() => {})
          }
        />
      )}
    </div>
  );
}
